import React from 'react'
import { useTranslation } from '@amusnet/translations/react'
import { formatCurrency } from '@amusnet/amusnet-common'
import closeButtonSvg from '@assets/close-button.svg'
import settingsButtonSvg from '@assets/ultra-play/ultra-play-settings.svg'
import UltraPlayStepper from './UltraPlayStepper'
import UltraPlayPlayPauseButton from './UltraPlayPlayPauseButton'
import UltraPlayFeatureSettings from './UltraPlayFeatureSettings'
import { playUltraPlaySound, ULTRA_PLAY_SOUND } from './ultraPlaySound'
import { UltraPlayControlsApi } from './useUltraPlayControls'

// Portrait, in-session controls: the gear-revealed settings band above the fixed 240px footer
// (BET stepper · play/pause · SPINS stepper · settings gear).
const UltraPlayPortraitFooter: React.FC<{ controls: UltraPlayControlsApi }> = ({ controls }) => {
    const { t } = useTranslation()
    const {
        currency,
        hideCents,
        currentBetAmount,
        spinsValue,
        selectedBetIndex,
        betOptions,
        isPlaying,
        stopOnFeature,
        setStopOnFeature,
        selectedBuyBonusFeatureName,
        buyBonusFeatures,
        hasBuyBonus,
        canDecrementSpins,
        canIncrementSpins,
        settingsOpen,
        setSettingsOpen,
        handleBetDecrement,
        handleBetIncrement,
        handleSpinsDecrement,
        handleSpinsIncrement,
        handleBuyBonusChange,
        handlePlayPause,
    } = controls

    return (
        <>
            {/* Settings band — revealed by the footer gear; sits above the play button, same #000000
                as the footer, covering the bottom of the table. */}
            {settingsOpen && (
                <div className="w-full flex-shrink-0 px-6 py-2 bg-black border-t border-t-ultra-border">
                    <UltraPlayFeatureSettings
                        variant="band"
                        isPortrait
                        stopOnFeature={stopOnFeature}
                        setStopOnFeature={setStopOnFeature}
                        hasBuyBonus={hasBuyBonus}
                        buyBonusFeatures={buyBonusFeatures}
                        buyBonusValue={selectedBuyBonusFeatureName}
                        onBuyBonusChange={handleBuyBonusChange}
                    />
                </div>
            )}

            <div className="w-full flex-shrink-0 flex items-center justify-between px-6 relative h-[240px] bg-black border-t border-t-ultra-border">
                <div className="flex flex-1 flex-col items-center">
                    <span className="font-bold text-[24px] leading-[24px] uppercase mb-[8px] tracking-[0.01em] text-ultra-label">
                        {t('gameui.bet')}
                    </span>
                    <div className="flex items-baseline gap-[6px] mb-[15px]">
                        <span className="font-bold text-[30px] leading-[36px] text-white">
                            {formatCurrency(currentBetAmount, { hideCents })}
                        </span>
                        <span className="font-bold text-[20px] leading-[24px] text-white">{currency}</span>
                    </div>
                    <UltraPlayStepper
                        onDecrement={handleBetDecrement}
                        onIncrement={handleBetIncrement}
                        canDecrement={selectedBetIndex > 0}
                        canIncrement={selectedBetIndex < betOptions.length - 1}
                    />
                </div>

                <UltraPlayPlayPauseButton
                    isPlaying={isPlaying}
                    onClick={handlePlayPause}
                    size={180}
                    className="flex-shrink-0"
                />

                <div className="flex flex-1 flex-col items-center">
                    <span className="font-bold text-[24px] leading-[24px] uppercase mb-[8px] tracking-[0.01em] text-ultra-label">
                        {t('common.spinsUppercase')}
                    </span>
                    <span className="font-bold text-[30px] leading-[36px] text-white mb-[15px]">{spinsValue}</span>
                    <UltraPlayStepper
                        onDecrement={handleSpinsDecrement}
                        onIncrement={handleSpinsIncrement}
                        canDecrement={canDecrementSpins}
                        canIncrement={canIncrementSpins}
                    />
                </div>

                <button
                    onClick={() => {
                        if (!settingsOpen) playUltraPlaySound(ULTRA_PLAY_SOUND.INFO_1)

                        setSettingsOpen((open) => !open)
                    }}
                    className="absolute bottom-5 right-5"
                    aria-label={settingsOpen ? 'Close settings' : 'Settings'}
                >
                    <img src={settingsOpen ? closeButtonSvg : settingsButtonSvg} className="w-[54px] h-[54px]" alt="" />
                </button>
            </div>
        </>
    )
}

export default UltraPlayPortraitFooter
